// Story 8.2 (ADO-2011), Slice 1 — the /cart page shell (GD-2). The extension points
// the later cart stories build on:
//   • `headerActions` — the slot beside the title (Story 8.9 mounts Clear here).
//   • `children`      — the content area (Story 8.11 replaces the default flat
//                       line list with the grouped Order Review).
//   • `banner`       : above the title (Story 8.5 mounts the cut-off banner here).
// The shell owns only the chrome: the catalogue-linking header (so search, browse
// and the cart trigger work here as on every screen) and the page title. Story 8.6's
// order-sent page passes no `title`: its own "Order sent" heading is the page's h1,
// named by `labelledBy`.
import type { ReactNode } from 'react';
import { useSessionStore } from '../../auth/useSessionStore';
import { isAdminRole } from '../../auth/rbac';
import { CatalogueLinkingHeader } from '../Products/CatalogueLinkingHeader';
import styles from './CartPage.module.css';

export interface CartPageShellProps {
  /** The page title (h1). Omit it when `children` renders the page's own h1. */
  readonly title?: string;
  /** The id of the element naming `<main>`; the title's own id when a title is given. */
  readonly labelledBy?: string;
  readonly headerActions?: ReactNode;
  readonly banner?: ReactNode;
  readonly children: ReactNode;
}

export function CartPageShell({
  title,
  labelledBy = 'cart-page-title',
  headerActions,
  banner,
  children,
}: CartPageShellProps) {
  const role = useSessionStore((s) => s.role);
  return (
    <div className={styles.page}>
      <CatalogueLinkingHeader showManagerDashboard={isAdminRole(role)} />
      <main className={styles.main} aria-labelledby={labelledBy}>
        <div className={styles.content}>
          {banner}
          {title !== undefined ? (
            <div className={styles.pageHead}>
              <h1 id="cart-page-title" className={styles.title} tabIndex={-1}>
                {title}
              </h1>
              {headerActions}
            </div>
          ) : null}
          {children}
        </div>
      </main>
    </div>
  );
}
